import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  MfgFinishVariantConsumeResponse,
  MfgFinishVariantDeficitsListResponse,
  MfgFinishVariantReattributeResponse,
  MfgMaterialUseCreateResponse,
  MfgMaterialUseDetailResponse,
  MfgMaterialUseDeleteResponse,
  MfgMaterialUseMemberUpdateResponse,
  MfgMaterialUseMemberUpsertResponse,
  MfgMaterialUsesListResponse,
  MfgMaterialUseUpdateResponse,
  MfgMaterialUseResolveMode,
} from "@/lib/manufacturing/types";

export function listManufacturingMaterialUses(opts?: { active_only?: boolean }) {
  const q = new URLSearchParams();
  if (opts?.active_only) q.set("active_only", "true");
  const qs = q.toString();
  return manufacturingFetch<MfgMaterialUsesListResponse>(
    qs ? `/api/mfg/material-uses?${qs}` : "/api/mfg/material-uses",
    { method: "GET" }
  );
}

export function getManufacturingMaterialUseDetail(
  useId: string,
  opts?: { plant_id?: string; active_only?: boolean }
) {
  const q = new URLSearchParams({ use_id: useId });
  if (opts?.plant_id) q.set("plant_id", opts.plant_id);
  if (opts?.active_only) q.set("active_only", "true");
  return manufacturingFetch<MfgMaterialUseDetailResponse>(
    `/api/mfg/material-uses/detail?${q.toString()}`,
    { method: "GET" }
  );
}

export function createManufacturingMaterialUse(payload: {
  use_code: string;
  name: string;
  unit?: string;
  notes?: string;
  resolve_mode?: MfgMaterialUseResolveMode;
  finish_channel?: string | null;
}) {
  return manufacturingFetch<MfgMaterialUseCreateResponse>("/api/mfg/material-uses/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateManufacturingMaterialUse(payload: {
  use_id: string;
  use_code?: string;
  name?: string;
  unit?: string;
  notes?: string;
  is_active?: boolean;
  resolve_mode?: MfgMaterialUseResolveMode;
  finish_channel?: string | null;
}) {
  return manufacturingFetch<MfgMaterialUseUpdateResponse>("/api/mfg/material-uses/update", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteManufacturingMaterialUse(payload: { use_id: string }) {
  return manufacturingFetch<MfgMaterialUseDeleteResponse>("/api/mfg/material-uses/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function upsertManufacturingMaterialUseMember(payload: {
  use_id: string;
  material_id: string;
  qty_per_use?: number;
  preference_rank?: number;
  is_active?: boolean;
  is_primary?: boolean;
  finish_code?: string | null;
}) {
  return manufacturingFetch<MfgMaterialUseMemberUpsertResponse>(
    "/api/mfg/material-uses/members/upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function updateManufacturingMaterialUseMember(payload: {
  member_id: string;
  qty_per_use?: number;
  preference_rank?: number;
  is_active?: boolean;
  is_primary?: boolean;
  finish_code?: string | null;
}) {
  return manufacturingFetch<MfgMaterialUseMemberUpdateResponse>(
    "/api/mfg/material-uses/members/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function deleteManufacturingMaterialUseMember(payload: { member_id: string }) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/material-uses/members/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export type MfgMaterialUsesBulkUpsertSummary = {
  created: Array<{ use_code: string; use_id: string; members: number }>;
  updated: Array<{ use_code: string; use_id: string; members: number }>;
  skipped: Array<{ use_code: string; reason: string }>;
};

export function bulkUpsertManufacturingMaterialUses(payload: {
  uses: Array<{
    use_code: string;
    use_name: string;
    unit?: string;
    notes?: string;
    is_active?: boolean;
    resolve_mode?: MfgMaterialUseResolveMode;
    finish_channel?: string | null;
    members?: Array<{
      material_code: string;
      qty_per_use?: number;
      preference_rank?: number;
      is_active?: boolean;
      is_primary?: boolean;
      finish_code?: string | null;
    }>;
  }>;
}) {
  return manufacturingFetch<{ ok: true; summary: MfgMaterialUsesBulkUpsertSummary }>(
    "/api/mfg/material-uses/bulk-upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export type MfgMaterialUsePromoteLinesResponse = {
  ok: true;
  lines: number;
  updated_count: number;
  member_created: boolean;
};

export function promoteManufacturingMaterialLinesToUse(payload: {
  from_material_id: string;
  material_use_id: string;
  preferred_material_id?: string;
  dry_run?: boolean;
}) {
  return manufacturingFetch<MfgMaterialUsePromoteLinesResponse>("/api/mfg/material-uses/promote-lines", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function listFinishVariantDeficits(opts?: { plant_id?: string; use_id?: string }) {
  const q = new URLSearchParams();
  if (opts?.plant_id) q.set("plant_id", opts.plant_id);
  if (opts?.use_id) q.set("use_id", opts.use_id);
  const qs = q.toString();
  return manufacturingFetch<MfgFinishVariantDeficitsListResponse>(
    qs
      ? `/api/mfg/material-uses/finish-variant/deficits?${qs}`
      : "/api/mfg/material-uses/finish-variant/deficits",
    { method: "GET" },
  );
}

export function consumeFinishVariantUse(payload: {
  use_id: string;
  plant_id: string;
  quantity: number;
  finish_modifiers?: Record<string, string>;
  serial_number?: string;
  deal_id?: string;
  built_unit_id?: string;
  mfg_unit_id?: string;
  notes?: string;
}) {
  return manufacturingFetch<MfgFinishVariantConsumeResponse>(
    "/api/mfg/material-uses/finish-variant/consume",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function reattributeFinishVariantDeficit(payload: {
  deficit_event_id: string;
  material_id: string;
}) {
  return manufacturingFetch<MfgFinishVariantReattributeResponse>(
    "/api/mfg/material-uses/finish-variant/reattribute",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}
